import { Logger } from "@suse-o11y/logger";
import { useDispatch } from "react-redux";

import type { StackPackResourceConfig } from "~/models/dto";

import {
	type ConfirmManualActionsStackPackData,
	confirmManualActionsStackPack,
	confirmManualActionsStackPackError,
	confirmManualActionsStackPackSuccess,
} from "../app/state/entities/stackPacks/StackPacksActions";
import { type FullStackPack, stackPackListFn } from "../common/openapi";
import { request } from "../generic/request";

import {
	type UseMutationOptions,
	type UseMutationResult,
	type UseQueryOptions,
	type UseQueryResult,
	useMutation,
	useQuery,
} from "./react-query";

export const useQueryStackpacksResourceConfig = <TResult = StackPackResourceConfig>(
	options?: UseQueryOptions<StackPackResourceConfig, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(["stackpacks", "resource-config"], () => request<StackPackResourceConfig>("/api/stackpack/config"), {
		staleTime: Number.POSITIVE_INFINITY,
		...options,
	});

// Map of text statuses by their URL
//
// Keying by URL works the best in a situation where multiple versions
// of a StackPack can be available with possibly different resources
export const useQueryStackpackTextByUrl = <TResult = string>(
	url: string,
	options?: UseQueryOptions<string, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(["stackpacks", "text", url], () => request(url, { accept: "markdown" }), {
		staleTime: Number.POSITIVE_INFINITY,
		...options,
	});

export const useStackPackConfirmManual = (
	options?: UseMutationOptions<void, Error, ConfirmManualActionsStackPackData>,
): UseMutationResult<void, Error, ConfirmManualActionsStackPackData> => {
	const dispatch = useDispatch();
	return useMutation(
		({ stackPack, configId }) =>
			request<void>(`/api/stackpack/${stackPack.name}/confirm-manual-steps/${configId}`, {
				method: "POST",
			}),
		{
			...options,
			onMutate: (variables) => {
				dispatch(confirmManualActionsStackPack(variables.stackPack, variables.configId));

				return options?.onMutate?.(variables);
			},
			onSuccess: (result, variables, context) => {
				dispatch(confirmManualActionsStackPackSuccess(variables.stackPack, variables.configId));

				return options?.onSuccess?.(result, variables, context);
			},
			onError: (error, variables, context) => {
				Logger.namespace("StackPacksConfirm").error(error.message);
				dispatch(confirmManualActionsStackPackError(variables.stackPack, variables.configId, error));

				return options?.onError?.(error, variables, context);
			},
		},
	);
};

export const useQueryStackPacks = <TResult = Array<FullStackPack>>(
	options?: UseQueryOptions<Array<FullStackPack>, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(["stackpacks"], () => stackPackListFn({}), {
		staleTime: Number.POSITIVE_INFINITY,
		...options,
	});
